// @ts-nocheck
import CustomButton from "@Common/CustomButton";
import { EditReservationWrapper } from "./styles";
import { useTranslation } from "react-i18next";
import { Modal, CircularProgress, Box } from "@mui/material";
import { FaArrowLeftLong, FaArrowRightLong } from "react-icons/fa6";
import useBreakPoinst from "@hooks/useBreakPoinst";
import {
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import ReservationInfo from "./components/reservationInfo";
import PriceData from "./components/priceData";
import GuestData from "./components/guestData";
import {
  APIS_KEYS,
  useGetListingManagement,
  useGetManualReservation,
  useGetUserDetail,
  usePutManualReservation,
  useTemplate,
} from "@services";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import { PopupOverlayNewCalender, reservationDetails } from "@store/index";
import { FormProvider, useForm } from "react-hook-form";
import { ToastMessageContext } from "@context/toast-message-cotnext";
import { useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import {
  formatPhoneForBackend,
  resolveDirectPaymentAccepted,
  stayTotalPrice,
} from "@utils";
interface IProps {
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
}
interface IForm {
  checkIn: string;
  checkOut: string;
  propertyId: string;
  checkInTime: string;
  checkOutTime: string;
  guestName: string;
  guestPhoneNumber: string;
  nightPrice: number;
  totalPrice: number;
  isUserPaymentActive: boolean;
}
export default function EditReservationPopup({ isOpen, setIsOpen }: IProps) {
  /* ------------------------------- Local State ------------------------------ */
  const [isActive, setIsActive] = useState<1 | 2 | 3>(1);
  const { isPhone } = useBreakPoinst();
  const reservationDetailsValue = useRecoilValue(reservationDetails);
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const tabButtons = [
    {
      id: 1,
      label: t("MANUALRESERVATION.RESERVATIONINFORMATION"),
      idForm: "ReservationInfoID",
    },
    {
      id: 2,
      label: t("MANUALRESERVATION.PRICINGDATA"),
      idForm: "PriceDataID",
    },
    {
      id: 3,
      label: t("MANUALRESERVATION.GUESTDATA"),
      idForm: "GuestDataID",
    },
  ];
  /* ---------------------------------- API'S --------------------------------- */
  const { data: dataGetManualReservation, isLoading: isLoadingReservation, isError: isErrorReservation } = useGetManualReservation(
    {
      id: reservationDetailsValue?.id!,
    },
    { enabled: reservationDetailsValue?.id !== undefined }
  );
  const { data: getListingManagement } = useGetListingManagement();
  const { mutateAsync: mutateAsyncPutManualReservation, isPending: isSubmitting } =
    usePutManualReservation();
  // Mirrors GuestData's toggle gating: direct payment needs both the host's
  // payment capability and a welcome-book template. GuestData renders the
  // toggle off when either is missing, so the payload must agree.
  const { data: userData } = useGetUserDetail();
  const { getTemplatesQuery } = useTemplate();
  const { data: templates } = getTemplatesQuery();
  /* ---------------------------------- Hooks --------------------------------- */
  const setPopupOverlayNewCalender = useSetRecoilState(PopupOverlayNewCalender);
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useContext(ToastMessageContext);
  const methods = useForm<IForm>();
  const { trigger, setValue, watch } = methods;
  const [kk, setReservationDetails] = useRecoilState(reservationDetails);

  const dataOfSections = useMemo(() => {
    setReservationDetails({
      ...kk,
      total: dataGetManualReservation?.result?.total,
      isDirectPaymentAccepted:
        dataGetManualReservation?.result?.isDirectPaymentAccepted ?? false,
    });
    const data = {
      ReservationInfoData: {
        checkIn: dataGetManualReservation?.result.checkIn,
        checkOut: dataGetManualReservation?.result.checkOut,
        propertyName: dataGetManualReservation?.result.propertyName,
        reservationId: dataGetManualReservation?.result.reservationId,
      },
      valueOfPriceData: {
        totalPrice: Number(dataGetManualReservation?.result.totalPrice),
        numberOfNights: dataGetManualReservation?.result.numberOfNights,
        checkIn: dataGetManualReservation?.result.checkIn,
        checkOut: dataGetManualReservation?.result.checkOut,
      },
      valueOfGuestData: {
        guestName: dataGetManualReservation?.result?.guestName,
        guestPhoneNumber: dataGetManualReservation?.result.guestPhoneNumber,
        // Seed from THIS reservation's direct-payment setting. The endpoint
        // returns no host-level `isUserPaymentActive`, so the old read was
        // always undefined: the toggle showed on while the form held false,
        // and saving an untouched direct-payment booking silently cleared it.
        isUserPaymentActive:
          dataGetManualReservation?.result?.isDirectPaymentAccepted ?? false,
      },
    };
    return data;
  }, [dataGetManualReservation]);

  const dataList = useMemo(() => {
    const data = getListingManagement?.result.map((item) => {
      return {
        value: item.propertyId.toString(),
        label: item.propertyName,
      };
    });

    return data ?? [];
  }, [getListingManagement]);
  /* -------------------------------- Functions ------------------------------- */
  const switchTab = async (stepId: 1 | 2 | 3) => {
    const isHaveError = await trigger([
      "checkIn",
      "checkOut",
      "propertyId",
      "checkInTime",
      "checkOutTime",
      "guestName",
      "guestPhoneNumber",
      "nightPrice",
      "isUserPaymentActive",
    ]);
    if (isHaveError) {
      setIsActive(stepId as 1 | 2 | 3);
    }
  };
  const onSubmit = async (value: IForm) => {
    const nameParts = value.guestName.trim().split(" ");
    const firstName = nameParts[0];
    const lastName = nameParts.slice(1).join(" ");

    const customGuestName = {
      firstName: firstName,
      lastName: lastName,
    };
    const { guestName, nightPrice, ...bookingWithoutGuestName } = value;
    const mergedData = {
      ...bookingWithoutGuestName,
      ...customGuestName,
      guestPhoneNumber: formatPhoneForBackend(value.guestPhoneNumber, "e164"),
      checkIn: dayjs(value.checkIn).format("YYYY-MM-DD"),
      checkOut: dayjs(value.checkOut).format("YYYY-MM-DD"),
      id: reservationDetailsValue?.id!,
      propertyName: dataOfSections.ReservationInfoData.propertyName,
      // Never send true when the toggle was rendered off/disabled — keyed on the
      // reservation's property so a property without a guest book can't trip the
      // backend's per-property guest-book check.
      isDirectPaymentAccepted: resolveDirectPaymentAccepted(
        (userData?.result as any)?.isUserPaymentActive,
        templates,
        Number(value.propertyId ?? reservationDetailsValue?.propertyId) ||
          undefined,
        value.isUserPaymentActive
      ),
    };
    const response = await mutateAsyncPutManualReservation({
      id: reservationDetailsValue?.id!,
      data: mergedData,
    });
    if (response.status >= 200 && response.status < 300) {
      setIsOpen(false);
      setPopupOverlayNewCalender(undefined);
      queryClient.invalidateQueries({ queryKey: [APIS_KEYS.GETCALENDERDATA] });
      showSuccess(t("COMMON.THE_RESERVATION_HAS_BEEN_MODIFIED"));
    }
    if (response.status === 400) {
      if (response?.data?.errors) {
        const merged: any = Object?.values(response?.data?.errors)?.reduce(
          (accumulator: any, currentValue) => accumulator.concat(currentValue),
          []
        );
        merged.map((item: any) => showError(item));
      }

      response?.data?.errorMessages &&
        response?.data?.errorMessages.map((item: any) =>
          showError(language === "ar" ? item.messageAr : item.messageEn)
        );
    }
    if (response.status === 500) {
      response?.data?.errorMessages.map((item: any) =>
        showError(language === "ar" ? item.messageAr : item.messageEn)
      );
    }
  };
  /* -------------------------------- useEffect ------------------------------- */

  useEffect(() => {
    if (dataOfSections.ReservationInfoData) {
      setValue("checkIn", dataOfSections.ReservationInfoData.checkIn ?? "");
      setValue("checkOut", dataOfSections.ReservationInfoData.checkOut ?? "");
    }

    if (
      dataOfSections.ReservationInfoData.propertyName &&
      dataList.length > 0
    ) {
      const listFilterd = dataList.filter(
        (item) => item.label === dataOfSections.ReservationInfoData.propertyName
      );
      setValue(
        "propertyId",
        listFilterd.length && listFilterd[0]?.value ? listFilterd[0]?.value : ""
      );
    }
  }, [dataOfSections.ReservationInfoData]);

  // Seed the nightly rate from the stay as it was booked. The total is NOT seeded here — it is
  // derived below from the rate and the dates currently in the form.
  useEffect(() => {
    if (dataOfSections.valueOfPriceData) {
      setValue(
        "nightPrice",
        Number(
          (dataOfSections.valueOfPriceData.totalPrice ?? 0) /
            (dataOfSections.valueOfPriceData.numberOfNights ?? 0)
        )
      );
    }
  }, [dataOfSections.valueOfPriceData]);

  // MS-589 — the total is the nightly rate times the nights the host has actually asked for.
  //
  // It used to be derived inside the price tab, from dates memoised on the API response: the
  // night count never moved when the host lengthened the stay, so the submitted total stayed at
  // the price already paid. The backend recognises an extension by exactly that difference, so an
  // unchanged total meant the extension was never detected — the guest got the ordinary
  // «تم تعديل حجزك» email instead of the payment request, against a stay that had grown a night.
  //
  // It lives here, in the form that owns the rate and both dates, because the price tab is
  // mounted only while it is open: a host who moved a date on the first tab and saved from there
  // submitted the loaded price no matter how the derivation itself was written.
  const watchNightPrice = watch("nightPrice");
  const watchCheckIn = watch("checkIn");
  const watchCheckOut = watch("checkOut");
  useEffect(() => {
    setValue(
      "totalPrice",
      stayTotalPrice(watchNightPrice, watchCheckIn, watchCheckOut)
    );
  }, [setValue, watchNightPrice, watchCheckIn, watchCheckOut]);

  useEffect(() => {
    if (dataOfSections.valueOfGuestData) {
      setValue("guestName", dataOfSections.valueOfGuestData?.guestName ?? "");
      setValue(
        "guestPhoneNumber",
        dataOfSections.valueOfGuestData?.guestPhoneNumber ?? ""
      );
      setValue(
        "isUserPaymentActive",
        dataOfSections.valueOfGuestData?.isUserPaymentActive ?? false
      );
    }
  }, [dataOfSections.valueOfGuestData]);

  return (
    <Modal
      open={isOpen}
      onClose={() => setIsOpen(false)}
      aria-labelledby="edit-reservation-title"
      // The create drawer does not dim the calendar behind it, so neither does
      // this one. Modal is kept for what it gives beyond the styling — Escape to
      // close, focus trap, and click-outside — which a bare fixed div would not.
      slotProps={{ backdrop: { sx: { backgroundColor: "transparent" } } }}
    >
      <EditReservationWrapper isRTL={language} $isPhone={isPhone}>
        <div>
          <div className="containerHeader">
            <div>
              <div className="navigate">
                <button type="button" onClick={() => setIsOpen(false)}>
                  {language === "ar" ? <FaArrowLeftLong /> : <FaArrowRightLong />}
                </button>
              </div>
              <div className="containerInfo">
                <p id="edit-reservation-title">{t("COMMON.BOOKING_UPDATED")}</p>
              </div>
            </div>
          </div>

          {!isLoadingReservation && !isErrorReservation && (
            <div className="containerTabs">
              {tabButtons.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  className={item.id === isActive ? "activeButton" : "normal"}
                  onClick={() => switchTab(item.id as 1 | 2 | 3)}
                >
                  {item.label}
                </button>
              ))}
            </div>
          )}

          <div>
            <FormProvider {...methods}>
              <form onSubmit={methods.handleSubmit(onSubmit)}>
                <div className="containerBody">
                  {isLoadingReservation ? (
                    <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
                      <CircularProgress size={40} sx={{ color: '#5b3fff' }} />
                    </Box>
                  ) : isErrorReservation ? (
                    <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" minHeight={200} gap={1}>
                      <span style={{ fontSize: 32 }}>⚠️</span>
                      <p style={{ margin: 0, fontWeight: 600, color: '#435363' }}>{t("COMMON.SOMETHING_WENT_WRONG", "Something went wrong")}</p>
                      <p style={{ margin: 0, fontSize: 13, color: '#8d9cae' }}>{t("COMMON.FAILED_TO_LOAD_RESERVATION", "Failed to load reservation data. Please close and try again.")}</p>
                    </Box>
                  ) : (
                    <>
                      {isActive === 1 && (
                        <ReservationInfo
                          dataList={dataList}
                          ReservationInfoData={
                            dataOfSections?.ReservationInfoData
                          }
                        />
                      )}
                      {isActive === 2 && <PriceData />}
                      {isActive === 3 && <GuestData />}
                    </>
                  )}
                </div>

                {!isLoadingReservation && !isErrorReservation && (
                  <div className="containerFooter">
                    <CustomButton
                      label={t("COMMON.SAVE_EDIT")}
                      withBG
                      borderRadius="8px"
                      customPadding="18px"
                      style={{ width: "100%" }}
                      type="submit"
                      loading={isSubmitting}
                      disabled={isSubmitting}
                    />
                    <CustomButton
                      label={t("COMMON.CANCEL_EDIT")}
                      borderRadius="8px"
                      customPadding="18px"
                      style={{ width: "100%" }}
                      borderColor="#5B3FFF"
                      customColor="#5B3FFF"
                      onClick={() => setIsOpen(false)}
                      disabled={isSubmitting}
                    />
                  </div>
                )}
              </form>
            </FormProvider>
          </div>
        </div>
      </EditReservationWrapper>
    </Modal>
  );
}
